import { useCallback, useEffect, useState } from 'react';
import {
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import type { AdminSession, Locale } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { formatDateTime } from '../viewer/format';
import { fetchUserSessions, revokeSession } from './sessions-api';

/**
 * Sessions ACTIVES d'un compte, dépliables depuis sa ligne dans « Comptes »
 * (audit B2) — superadmin.
 *
 * POURQUOI. La révocation n'existait que comme EFFET DE BORD : pour déconnecter
 * quelqu'un, le superadmin devait lui réinitialiser son mot de passe ou supprimer son
 * compte — sans même savoir qui était connecté. Avec le cookie persistant et la session
 * glissante de 30 jours, l'angle mort était réel.
 *
 * VIE PRIVÉE. On n'affiche que ce qui sert à DÉCIDER quelle session couper : agent
 * utilisateur, horodatages, lien d'accès d'origine. Aucune adresse IP — la table
 * `sessions` n'en stocke pas (rien n'est masqué, la donnée n'existe pas) — et jamais le
 * jeton ni son empreinte.
 */

export function UserSessionsPanel({ userId }: { userId: string }): React.JSX.Element {
  const { t, locale } = useI18n();
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading');
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetchUserSessions(userId, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        setSessions(res.sessions);
        setStatus('ready');
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setStatus('error');
      });
    return () => controller.abort();
  }, [userId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const onRevoke = (sessionId: string): void => {
    if (busyId !== null) return;
    setBusyId(sessionId);
    setError(null);
    void revokeSession(sessionId)
      .then(() => {
        // Retrait optimiste : la session révoquée n'est plus « active », elle sort donc
        // de la liste. Un rechargement complet n'apporterait rien de plus ici.
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      })
      .catch(() => setError(t('admin.sessions.revokeError')))
      .finally(() => setBusyId(null));
  };

  if (status === 'loading') {
    return (
      <p role="status" className="py-2 text-xs text-muted">
        {t('admin.loading')}
      </p>
    );
  }
  if (status === 'error') {
    return (
      <div className="flex items-center gap-2 py-2">
        <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
        <p className="text-xs text-danger">{t('admin.sessions.loadError')}</p>
        <button
          type="button"
          onClick={reload}
          className="rounded-md px-2 py-1 text-xs text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          {t('admin.retry')}
        </button>
      </div>
    );
  }
  if (sessions.length === 0) {
    return <p className="py-2 text-xs text-muted">{t('admin.sessions.empty')}</p>;
  }

  return (
    <div className="flex w-full flex-col gap-2 rounded-md border border-border p-3 text-start">
      <p className="text-xs font-medium text-fg">
        {interpolate(t('admin.sessions.count'), { count: sessions.length }, locale)}
      </p>
      <ul className="flex flex-col gap-2">
        {sessions.map((session) => (
          <SessionRow
            key={session.id}
            session={session}
            locale={locale}
            busy={busyId === session.id}
            onRevoke={() => onRevoke(session.id)}
          />
        ))}
      </ul>
      {error !== null && (
        <p role="alert" className="inline-flex items-center gap-1 text-xs text-danger">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}

function SessionRow({
  session,
  locale,
  busy,
  onRevoke,
}: {
  session: AdminSession;
  locale: Locale;
  busy: boolean;
  onRevoke: () => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const last = session.lastUsedAt ?? session.createdAt;
  const lastLabel = formatDateTime(last, locale) ?? last;
  const expires = formatDateTime(session.expiresAt, locale) ?? session.expiresAt;

  return (
    <li className="flex flex-wrap items-start gap-2 border-b border-border pb-2 last:border-b-0 last:pb-0">
      <ComputerDesktopIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {/* L'agent utilisateur est le SEUL discriminant entre deux sessions du même
            compte : il porte l'identification, pas la décoration. `break-all` : ces
            chaînes sont longues et sans espaces. */}
        <p className="break-all text-xs text-fg">
          {session.userAgent ?? t('admin.sessions.unknownAgent')}
        </p>
        <p className="text-xs text-muted">
          {interpolate(t('admin.sessions.lastUsed'), { when: lastLabel })}
        </p>
        <p className="text-xs text-muted">
          {interpolate(t('admin.sessions.expires'), { when: expires })}
        </p>
        {/* Origine « lien d'accès » : c'est ce qui rend comptable la diffusion d'un
            lien communautaire (combien de sessions il a ouvertes, et laquelle couper
            sans révoquer le lien entier). */}
        {session.sessionLinkId !== null && (
          <p className="flex items-center gap-1 text-xs text-muted">
            <LinkIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {interpolate(t('admin.sessions.viaLink'), {
              label: session.sessionLinkLabel ?? t('admin.sessions.unknownLink'),
            })}
          </p>
        )}
        {session.current && (
          <p className="text-xs font-medium text-brand">{t('admin.sessions.current')}</p>
        )}
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onRevoke}
        className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-danger hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:opacity-50"
      >
        <NoSymbolIcon className="h-4 w-4" aria-hidden="true" />
        {busy ? t('admin.sessions.revoking') : t('admin.sessions.revoke')}
      </button>
    </li>
  );
}
